import { useId, useRef, useState } from "react"
import type { ReactNode, SelectHTMLAttributes } from "react"
import { AlertCircle, Inbox, Loader2, X } from "lucide-react"
import { toast } from "sonner"
import { formatInTimeZone, fromZonedTime } from "date-fns-tz"
import { ConvexError } from "convex/values"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"

export const formatDate = (value?: number | null) =>
	value
		? new Intl.DateTimeFormat("en-GB", {
				dateStyle: "medium",
				timeStyle: "short",
				timeZone: "Asia/Nicosia",
			}).format(value)
		: "—"
export const number = (value: number) => new Intl.NumberFormat("en-GB").format(value)
export const localDateTime = (value?: number, timezone?: string) => {
	if (value !== undefined && !Number.isFinite(value)) return ""
	if (timezone) {
		try {
			return formatInTimeZone(value ?? Date.now(), timezone, "yyyy-MM-dd'T'HH:mm")
		} catch {
			return ""
		}
	}
	const d = new Date(value ?? Date.now())
	return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}
export const parseDateTime = (value: string, timezone: string) =>
	fromZonedTime(value, timezone).getTime()
export function PageHeading({
	title,
	description,
	actions,
}: {
	title: string
	description?: string
	actions?: ReactNode
}) {
	return (
		<div className="page-heading">
			<div>
				<h1>{title}</h1>
				{description && <p>{description}</p>}
			</div>
			{actions && <div className="actions">{actions}</div>}
		</div>
	)
}
export function Panel({
	title,
	actions,
	children,
	className = "",
}: {
	title?: string
	actions?: ReactNode
	children: ReactNode
	className?: string
}) {
	return (
		<section className={`panel ${className}`}>
			{title && (
				<div className="panel-header">
					<h2>{title}</h2>
					{actions}
				</div>
			)}
			{children}
		</section>
	)
}
export function Badge({ children }: { children: ReactNode }) {
	const value = String(children).toLowerCase()
	const style = [
		"active",
		"delivered",
		"complete",
		"completed",
		"subscribed",
		"ready",
		"sent",
	].includes(value)
		? "good"
		: ["paused", "pending", "queued", "scheduled", "sending", "processing", "delayed"].includes(
					value,
			  )
			? "warn"
			: [
						"bounced",
						"failed",
						"suppressed",
						"complained",
						"canceled",
						"unsubscribed",
						"archived",
				  ].includes(value)
				? "bad"
				: ""
	return <span className={`badge ${style}`}>{children}</span>
}
export function Field({
	label,
	hint,
	children,
	className = "",
}: {
	label: string
	hint?: string
	children: ReactNode
	className?: string
}) {
	return (
		<div className={`field ${className}`}>
			<label>
				{label}
				{children}
			</label>
			{hint && <small>{hint}</small>}
		</div>
	)
}
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
	return <select {...props} className={`select ${props.className ?? ""}`} />
}
export function Empty({
	title,
	description,
	action,
	icon,
}: {
	title: string
	description?: string
	action?: ReactNode
	icon?: ReactNode
}) {
	return (
		<div className="empty">
			<div className="empty-icon">{icon ?? <Inbox size={22} />}</div>
			<h3>{title}</h3>
			{description && <p>{description}</p>}
			{action}
		</div>
	)
}
export function Loading() {
	return (
		<div className="loading" role="status">
			<Loader2 className="animate-spin" size={19} />
			Loading…
		</div>
	)
}
export function Notice({ children, error = false }: { children: ReactNode; error?: boolean }) {
	return (
		<div className={`notice ${error ? "error" : ""}`} role={error ? "alert" : undefined}>
			<AlertCircle size={16} />
			<div>{children}</div>
		</div>
	)
}
export function Modal({
	title,
	description,
	open,
	onClose,
	children,
	wide = false,
}: {
	title: string
	description?: string
	open: boolean
	onClose: () => void
	children: ReactNode
	wide?: boolean
}) {
	return (
		<Dialog
			open={open}
			onOpenChange={(value) => {
				if (!value) onClose()
			}}
		>
			<DialogContent
				className={`max-h-[90dvh] overflow-y-auto p-6 ${wide ? "sm:max-w-4xl" : "sm:max-w-xl"}`}
			>
				<DialogHeader>
					<DialogTitle className="text-xl">{title}</DialogTitle>
					{description && <DialogDescription>{description}</DialogDescription>}
				</DialogHeader>
				{children}
			</DialogContent>
		</Dialog>
	)
}
export function SaveButton({
	pending,
	children = "Save changes",
	...rest
}: {
	pending: boolean
	children?: ReactNode
	disabled?: boolean
	onClick?: () => void
	type?: "button" | "submit"
}) {
	return (
		<Button type="submit" {...rest} disabled={pending || rest.disabled} className="h-9 px-4">
			{pending && <Loader2 size={14} className="animate-spin" />}
			{children}
		</Button>
	)
}
export function useTask() {
	const [pending, setPending] = useState(false)
	const running = useRef(false)
	async function run(action: () => Promise<unknown>, message?: string) {
		if (running.current) return false
		running.current = true
		setPending(true)
		try {
			await action()
			if (message) toast.success(message)
			return true
		} catch (error) {
			toast.error(
				error instanceof ConvexError && typeof error.data === "string"
					? error.data
					: error instanceof Error
						? error.message
						: "Something went wrong. Please try again.",
			)
			return false
		} finally {
			running.current = false
			setPending(false)
		}
	}
	return { pending, run }
}
export function Check({
	label,
	checked,
	onChange,
	hint,
}: {
	label: string
	checked: boolean
	onChange: (value: boolean) => void
	hint?: string
}) {
	const id = useId()
	return (
		<div>
			<label className="check-row" htmlFor={id}>
				<input
					id={id}
					type="checkbox"
					checked={checked}
					onChange={(e) => onChange(e.target.checked)}
				/>
				{label}
			</label>
			{hint && <p className="muted ml-6 mt-1 text-xs">{hint}</p>}
		</div>
	)
}
export function Stat({
	label,
	value,
	note,
	icon,
}: {
	label: string
	value: ReactNode
	note?: string
	icon?: ReactNode
}) {
	return (
		<div className="panel stat">
			<div className="stat-label">
				{label}
				{icon}
			</div>
			<div className="stat-value">{value}</div>
			{note && <div className="stat-note">{note}</div>}
		</div>
	)
}
export function Tabs({
	items,
	value,
	onChange,
}: {
	items: string[]
	value: string
	onChange: (value: string) => void
}) {
	return (
		<div className="tabs">
			{items.map((item) => (
				<button
					type="button"
					key={item}
					className={value === item ? "selected" : ""}
					onClick={() => onChange(item)}
				>
					{item}
				</button>
			))}
		</div>
	)
}
export function RemoveButton({
	onClick,
	label = "Remove",
}: {
	onClick: () => void
	label?: string
}) {
	return (
		<Button type="button" variant="ghost" size="icon" onClick={onClick} aria-label={label}>
			<X size={15} />
		</Button>
	)
}
